import React from "react";
import {
  View,
  Text,
  TouchableOpacity,
  Modal,
  StyleSheet,
  Platform,
  Dimensions,
} from "react-native";
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withSpring,
  withTiming,
  runOnJS,
} from "react-native-reanimated";
import { Ionicons } from "@expo/vector-icons";
import { useEffect } from "react";

const { width: SCREEN_WIDTH } = Dimensions.get("window");

export interface AlertButton {
  text: string;
  style?: "default" | "cancel" | "destructive";
  onPress?: () => void;
}

export interface CustomAlertProps {
  visible: boolean;
  title: string;
  message: string;
  buttons?: AlertButton[];
  onDismiss: () => void;
  icon?: keyof typeof Ionicons.glyphMap;
  iconColor?: string;
}

const CustomAlert: React.FC<CustomAlertProps> = ({
  visible,
  title,
  message,
  buttons = [{ text: "OK", style: "default" }],
  onDismiss,
  icon,
  iconColor = "rgba(19, 121, 162, 1)",
}) => {
  const scale = useSharedValue(0.8);
  const opacity = useSharedValue(0);

  useEffect(() => {
    if (visible) {
      scale.value = withSpring(1, { damping: 15, stiffness: 150 });
      opacity.value = withTiming(1, { duration: 200 });
    } else {
      scale.value = withTiming(0.8, { duration: 150 });
      opacity.value = withTiming(0, { duration: 150 });
    }
  }, [visible]);

  const animatedStyle = useAnimatedStyle(() => ({
    transform: [{ scale: scale.value }],
    opacity: opacity.value,
  }));

  const handleButtonPress = (button: AlertButton) => {
    onDismiss();
    button.onPress?.();
  };

  const getButtonStyle = (style?: string) => {
    switch (style) {
      case "destructive":
        return { bg: "#FEE2E2", text: "#DC2626", border: "#FECACA" };
      case "cancel":
        return { bg: "#F3F4F6", text: "#6B7280", border: "#E5E7EB" };
      default:
        return {
          bg: "rgba(19, 121, 162, 0.1)",
          text: "rgba(19, 121, 162, 1)",
          border: "rgba(19, 121, 162, 0.2)",
        };
    }
  };

  if (!visible) return null;

  return (
    <Modal transparent animationType="none" visible={visible}>
      <View style={styles.overlay}>
        <Animated.View
          style={[
            styles.container,
            animatedStyle,
            Platform.OS === "web" && { maxWidth: 400 },
          ]}
        >
          {/* Icon */}
          {icon && (
            <View
              style={[styles.iconCircle, { backgroundColor: `${iconColor}15` }]}
            >
              <Ionicons name={icon} size={28} color={iconColor} />
            </View>
          )}

          {/* Title */}
          <Text style={styles.title}>{title}</Text>

          {/* Message */}
          <Text style={styles.message}>{message}</Text>

          {/* Buttons */}
          <View
            style={[
              styles.buttonRow,
              buttons.length === 1 && { justifyContent: "center" },
            ]}
          >
            {buttons.map((button, index) => {
              const colors = getButtonStyle(button.style);
              return (
                <TouchableOpacity
                  key={index}
                  activeOpacity={0.7}
                  onPress={() => handleButtonPress(button)}
                  style={[
                    styles.button,
                    {
                      backgroundColor: colors.bg,
                      borderColor: colors.border,
                    },
                    buttons.length > 1 && { flex: 1 },
                  ]}
                >
                  <Text
                    style={[
                      styles.buttonText,
                      { color: colors.text },
                      button.style === "destructive" && { fontWeight: "700" },
                    ]}
                  >
                    {button.text}
                  </Text>
                </TouchableOpacity>
              );
            })}
          </View>
        </Animated.View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    justifyContent: "center",
    alignItems: "center",
    padding: 24,
  },
  container: {
    backgroundColor: "white",
    borderRadius: 20,
    padding: 24,
    width: "100%",
    maxWidth: SCREEN_WIDTH - 48,
    alignItems: "center",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.15,
    shadowRadius: 24,
    elevation: 12,
  },
  iconCircle: {
    width: 56,
    height: 56,
    borderRadius: 28,
    justifyContent: "center",
    alignItems: "center",
    marginBottom: 16,
  },
  title: {
    fontSize: 18,
    fontWeight: "700",
    color: "#111827",
    textAlign: "center",
    marginBottom: 8,
    fontFamily: "kawkabBold",
  },
  message: {
    fontSize: 14,
    color: "#6B7280",
    textAlign: "center",
    lineHeight: 21,
    marginBottom: 24,
    fontFamily: "kawkabReg",
  },
  buttonRow: {
    flexDirection: "row",
    gap: 10,
    width: "100%",
  },
  button: {
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 12,
    borderWidth: 1,
    alignItems: "center",
    justifyContent: "center",
  },
  buttonText: {
    fontSize: 14,
    fontWeight: "600",
    fontFamily: "kawkabBold",
  },
});

export default CustomAlert;
